import React, { useState, useRef, forwardRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  Typography,
  Paper,
  Button,
  Stack,
  Grid,
  Slide,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Box
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import axiosInstance from '../../../hooks/axiosInstance';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const Transition = forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

SpectrometerGraph.propTypes = {
  color: PropTypes.string,
  title: PropTypes.string.isRequired,
  spectrometerPlot: PropTypes.array,
  spectrometerReference: PropTypes.array,
  spectrometerReferencePath: PropTypes.string, // Add this prop
  sx: PropTypes.object,
};

export default function SpectrometerGraph({
  title,
  color = 'primary',
  spectrometerPlot,
  spectrometerReference,
  spectrometerReferencePath, // Add this prop
  sx,
  ...other
}) {
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState('');
  const callbackTimeout = useRef(null);

  const handleClose = () => {
    setOpen(false);
    clearTimeout(callbackTimeout.current);
  };

  const triggerSpectrumMeasurement = () => {
    const deviceId = localStorage.getItem('device_id');
    const jwtToken = localStorage.getItem('jwtToken');

    axiosInstance
      .post(`/condition/setpoint/${deviceId}`, {
        take_spectrum: 'spectrum',
      })
      .then((res) => {
        if (res.status === 200) {
          clearTimeout(callbackTimeout.current);
          setError(2); // loading
          setMsg('Waiting for device to take spectrum measurement...');
          setOpen(true);

          const subEventSource = new EventSource(
            `https://demo.smartfarm.id/condition/get-events-callback/${deviceId}?jwt_token=${jwtToken}`
          );

          subEventSource.onmessage = (event) => {
            const data = JSON.parse(event.data);
            if (data.message === 'Spectrum command received') {
              setMsg('Spectrum measurement successful. You can refresh the page to see the new data.');
              setError(0); // success
              subEventSource.close();
              clearTimeout(callbackTimeout.current);
            }
          };

          callbackTimeout.current = setTimeout(() => {
            setMsg('Spectrum measurement failed or timed out. Please try again.');
            setError(1); // failure
            subEventSource.close();
          }, 20000);
        }
      })
      .catch((err) => {
        setMsg(err.response?.data?.msg || 'An error occurred');
        setError(1); // failure
        setOpen(true);
      });
  };

  const [timestamp, setTimestamp] = useState(Date.now());
    useEffect(() => {
      const interval = setInterval(() => {
        setTimestamp(Date.now());
      }, 10000); // update every 10 seconds
  
      return () => clearInterval(interval);
    }, []);

  return (
    <Card
      sx={{
        py: 2,
        px: 2,
        boxShadow: 0,
        textAlign: 'center',
        color: (theme) => theme.palette[color].darker,
        bgcolor: (theme) => theme.palette[color].lighter,
        ...sx,
      }}
      {...other}
    >
      <Typography variant="h6">
        {title}
      </Typography>
      
      <Grid container spacing={0} mb = {2}>
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              bgcolor: (theme) => theme.palette[color].lighter,
              textAlign: 'center',
              p: 2,
            }}
          >
            <h3>Expected Spectrum</h3>
            {/* Use SSE path if available, otherwise fallback to array */}
            {spectrometerReferencePath ? (
              <Box
                mb={2}
                sx={{
                  width: 400,
                  height: 300,
                  margin: '0 auto',
                  boxShadow: 3,
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                <img
                  src={`https://demo.smartfarm.id${spectrometerReferencePath}?t=${timestamp}`}
                  alt="Reference Spectrum"
                  width="400"
                  height="300"
                  style={{ objectFit: 'cover', display: 'block' }}
                />
              </Box>
            ) : (
              spectrometerReference.length > 0 && (
                <Box
                  mb={2}
                  sx={{
                    width: 400,
                    height: 300,
                    margin: '0 auto',
                    boxShadow: 3,
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  <img
                    src={`https://demo.smartfarm.id${spectrometerReference[spectrometerReference.length - 2].file_url}?t=${timestamp}`}
                    alt="Reference"
                    width="400"
                    height="300"
                    style={{ objectFit: 'cover', display: 'block' }}
                  />
                </Box>
              )
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              bgcolor: (theme) => theme.palette[color].lighter,
              textAlign: 'center',
              p: 2,
            }}
          >
            <h3>Captured Spectrum</h3>
            {spectrometerPlot.map((item, i) => (
              <Box
                key={i}
                mb={2}
                sx={{
                  width: 400,
                  height: 300,
                  margin: '0 auto',
                  boxShadow: 3,
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                <img
                  src={`https://demo.smartfarm.id${item.file_url}?t=${timestamp}`}
                  alt={`Spectrum ${i}`}
                  width="400"
                  height="300"
                  style={{ objectFit: 'cover', display: 'block' }}
                />
                <Typography variant="p" sx={{ opacity: 0.72 }}>
                Timestamp: {item.uploaded_at.match(/(?<=)(.*)(?=T)/gm)} {item.uploaded_at.match(/(?<=T)(.*)(?=:)/gm)}
                </Typography>
              </Box>
            ))}
          </Paper>
        </Grid>
      </Grid>

      <Stack direction="row" spacing={2} justifyContent="center" mb={2}>
        <Box sx={{textAlign: 'center', mb:2}}>
          {spectrometerPlot.length > 0 && (
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Last Captured at:{' '}
              {new Date(spectrometerPlot[spectrometerPlot.length - 1].uploaded_at).toLocaleString()}
            </Typography>
          )}

          <Button
            size="medium"
            variant="contained"
            onClick={triggerSpectrumMeasurement}
            sx={{ minWidth: 200 }}
            aria-label="Take spectrum measurement"
          >
            Take Spectrum Measurement
          </Button>
        </Box>
      </Stack>

      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle>
          <Typography variant="h5" align="center">
            {error === 1 && 'Failure'}
            {error === 0 && 'Success'}
            {error === 2 && 'Loading...'}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ textAlign: 'center' }}>
          <DialogContentText id="alert-dialog-slide-description">{msg}</DialogContentText>
          {error === 2 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'center' }}>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}